// Plain inputs and outputs of the music engine. No audio objects here: the rest of the app speaks
// only these types.

export const SCREENS = ['loading', 'leader-select', 'game', 'sandbox'] as const;
export type Screen = (typeof SCREENS)[number];

/** What the game is doing, as far as music cares. */
export interface MusicState {
  screen: Screen;
  atWar: boolean;
}

export const EVENTS = ['war-declared', 'deal-signed', 'turn-advanced', 'victory-opened'] as const;
export type MusicEvent = (typeof EVENTS)[number];

export const BED_CUES = ['theme', 'explore', 'tension', 'sandbox'] as const;
export type BedCue = (typeof BED_CUES)[number];

export const STING_CUES = ['war', 'deal', 'turn', 'victory'] as const;
export type StingCue = (typeof STING_CUES)[number];

export type CueId = BedCue | StingCue;

export const SOURCES = ['orchestra', 'cc0'] as const;
export type Source = (typeof SOURCES)[number];

export const CHANNELS = ['master', 'music', 'effects'] as const;
export type Channel = (typeof CHANNELS)[number];

/** Settings the player controls; persisted. Volumes are slider positions, 0..1. */
export interface Settings {
  master: number;
  music: number;
  effects: number;
  muted: boolean;
  source: Source;
}

export type MuteMode = 'on' | 'off' | 'toggle';

/**
 * locked       no user gesture yet: the browser would refuse to play
 * ready        sound can play
 * unavailable  no WebAudio, or the manifest could not be loaded
 */
export type MusicStatus = 'locked' | 'ready' | 'unavailable';

export interface MusicSnapshot {
  settings: Settings;
  status: MusicStatus;
  /** The bed cue now playing (or fading in), null when silent. */
  bed: BedCue | null;
}

export interface Music {
  /** Tell the engine what the game is doing; it picks and crossfades the bed. Safe to call often. */
  setState(state: Partial<MusicState>): void;
  /** Play the sting for something that just happened. */
  cue(event: MusicEvent): void;
  /** Set one volume slider, 0..1. */
  setVolume(channel: Channel, value: number): void;
  setSource(source: Source): void;
  mute(mode: MuteMode): void;
  getSnapshot(): MusicSnapshot;
  /** Listener runs after every change to the snapshot; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void;
  /** Start sound from a user gesture the engine cannot see (it already listens to the page). */
  unlock(): void;
  dispose(): void;
}
